// Tiny REST client — always sends cookies (credentials: 'include') so JWT
// session works automatically.
// T164 — `opts.signal` (an AbortController signal) lets callers cancel a
// superseded request so a slow earlier response can't overwrite a newer one.
async function request(method, path, body, opts = {}) {
  const init = {
    method,
    credentials: 'include',
    headers: { Accept: 'application/json' },
    ...(opts.signal ? { signal: opts.signal } : {}),
  };
  if (body instanceof FormData) {
    // T266(5)/T271 — multipart. Deliberately NO Content-Type: the browser has
    // to set it itself so it can add the multipart boundary. That single detail
    // is why three call sites hand-rolled their own fetch, and with it they
    // each lost the 401 handling and the error shaping below — an expired
    // session during a CV upload dead-ended on the upload instead of bouncing
    // to /login.
    init.body = body;
  } else if (body !== undefined) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const res = await fetch(`/api${path}`, init);
  const text = await res.text();
  const parsed = text ? safeJson(text) : null;
  if (!res.ok) {
    // T160 — a 401 on any authed call means the JWT session expired or was
    // revoked. Announce it so AuthProvider can clear `user` and RequireAuth can
    // bounce to /login, instead of leaving the operator staring at stale data
    // while every subsequent action throws a red error toast into a dead end.
    // Skip the auth probes themselves: a failed /auth/login (wrong password) and
    // the initial /auth/me check (not-logged-in) own their handling.
    if (path !== '/auth/login' && path !== '/auth/me') announceUnauthorized(res.status);
    const err = new Error(humanMessage(parsed, res.status));
    err.status = res.status;
    err.body = parsed;
    throw err;
  }
  return parsed;
}

/**
 * T306 — the one thing every authed call must do on a 401, in one place.
 *
 * Extracted from `request` so a caller that CANNOT go through it still gets it.
 * The comment above about multipart says why that matters: three call sites
 * hand-rolled their own fetch for FormData and each silently lost this, so an
 * expired session dead-ended on the upload instead of bouncing to /login.
 * Binary downloads are the same shape — `request` reads the body as text, which
 * a .docx or .pdf is not.
 */
export function announceUnauthorized(status) {
  if (status !== 401 || typeof window === 'undefined') return false;
  window.dispatchEvent(new CustomEvent('hunter:unauthorized'));
  return true;
}

/**
 * A binary GET with a real deadline and the 401 announcement.
 *
 * `fetch` has no default timeout, so a request that never settles leaves its
 * caller's button disabled forever — measured on 2026-09-23, when a wedged
 * LibreOffice held two PDF downloads for the full ten minutes of Apache's
 * ProxyTimeout (see T301/T303). `AbortSignal.timeout` makes that a visible
 * error instead.
 *
 * Lives here rather than beside the modal for two reasons: this file owns the
 * 401 contract, and a component module that exports non-components stops being
 * a React Fast Refresh boundary — every edit to the modal would full-reload the
 * dev page and discard whatever the operator had typed into it.
 */
export async function fetchBinaryWithTimeout(path, { timeoutMs, what }) {
  let res;
  try {
    res = await fetch(path, { credentials: 'include', signal: AbortSignal.timeout(timeoutMs) });
  } catch (e) {
    // TimeoutError is what AbortSignal.timeout throws. An AbortError would be a
    // caller cancelling, which nothing here does; anything else is the network.
    if (e?.name === 'TimeoutError') {
      throw new Error(
        `${what} timed out after ${Math.round(timeoutMs / 1000)}s — the server never answered. Try again.`,
      );
    }
    throw e;
  }
  announceUnauthorized(res.status);
  return res;
}

// T249 — turn a failure into a sentence, once, here.
//
// The error carried `parsed.error`, which is a SLUG. Twenty-five backend routes
// answer a validation failure with `{ error: 'invalid_body', issues }` and no
// `message` at all, so every form in the app showed the operator the literal
// string "invalid_body" — or nothing, for the call sites reading
// `body?.message`. The issues were right there and no frontend code read them.
//
// Composed centrally rather than per form: there are 79 extraction sites across
// 26 files, and a contract enforced in one place is a contract.
function humanMessage(parsed, status) {
  if (parsed?.message) return parsed.message;

  const issue = Array.isArray(parsed?.issues) ? parsed.issues[0] : null;
  if (issue) {
    // Zod's `path` is the field the operator actually filled in; without it
    // "Required" is true and useless.
    const field = Array.isArray(issue.path) ? issue.path.filter((p) => p !== '').join('.') : '';
    const detail = issue.message || 'is not valid';
    const more = parsed.issues.length > 1 ? ` (+${parsed.issues.length - 1} more)` : '';
    return field ? `${field}: ${detail}${more}` : `${detail}${more}`;
  }

  // A bare slug is still better than "http_400", but only just — so give the
  // ones the operator can actually meet a sentence.
  const slug = parsed?.error;
  if (slug && KNOWN[slug]) return KNOWN[slug];
  if (slug) return slug.replace(/_/g, ' ');
  return `Request failed (${status})`;
}

const KNOWN = {
  invalid_body: 'Some of those values were not accepted.',
  invalid_query: 'That filter combination was not accepted.',
  invalid_id: 'That item id is not valid.',
  not_found: 'That item no longer exists.',
  anthropic_unauthorized: 'Set your Anthropic API key in Settings → Connectors first.',
  rate_limited: 'Too many AI requests in a row — wait a moment and retry.',
};

/**
 * The one way to get a displayable string out of a thrown request.
 *
 * Rewritten inline 79 times across 26 files, with divergent fallbacks — some
 * reading `body.message` (usually absent), some `body.error` (a slug), some
 * `e.message`. They now all agree because they all call this.
 */
export function errMsg(e, fallback = 'Something went wrong') {
  return e?.body?.message || e?.message || fallback;
}

function safeJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  get: (path, opts) => request('GET', path, undefined, opts),
  post: (path, body, opts) => request('POST', path, body ?? {}, opts),
  put: (path, body, opts) => request('PUT', path, body ?? {}, opts),
  patch: (path, body, opts) => request('PATCH', path, body ?? {}, opts),
  delete: (path, opts) => request('DELETE', path, undefined, opts),
  /** Multipart POST. `fields` become form fields alongside the file. */
  upload: (path, file, fields = {}, opts) => {
    const fd = new FormData();
    fd.append('file', file);
    for (const [k, v] of Object.entries(fields)) fd.append(k, v);
    return request('POST', path, fd, opts);
  },
};
